import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService, ENV_CONFIG, EnvironmentConfig } from '@beautyapp/core';
import { VisitPhoto } from '../models/customer-360.types';

/**
 * Visit photos service. Upload uses `HttpClient` directly because the
 * shared `ApiService.post` JSON-serializes the body, and photo upload
 * needs a raw `FormData` (multipart/form-data boundary set by the
 * browser).
 */
@Injectable({ providedIn: 'root' })
export class VisitPhotosService {
  private api = inject(ApiService);
  private http = inject(HttpClient);
  private config = inject<EnvironmentConfig>(ENV_CONFIG);

  listByBooking(bookingId: string): Observable<VisitPhoto[]> {
    return this.api.get<VisitPhoto[]>(`visit-photos`, { bookingId });
  }

  listByVisit(visitId: number): Observable<VisitPhoto[]> {
    return this.api.get<VisitPhoto[]>(`visit-photos`, { visitId });
  }

  listByCustomer(
    userId: string,
    params?: { limit?: number; offset?: number },
  ): Observable<VisitPhoto[]> {
    return this.api.get<VisitPhoto[]>(
      `visit-photos/by-customer/${userId}`,
      params,
    );
  }

  upload(file: File, meta: {
    bookingId?: string;
    visitId?: number;
    caption?: string;
    isCover?: boolean;
  }): Observable<VisitPhoto> {
    const body = new FormData();
    body.append('file', file);
    if (meta.bookingId) body.append('bookingId', meta.bookingId);
    if (meta.visitId != null) body.append('visitId', String(meta.visitId));
    if (meta.caption) body.append('caption', meta.caption);
    if (meta.isCover != null) body.append('isCover', String(meta.isCover));
    return this.http.post<VisitPhoto>(
      `${this.config.baseUrl}visit-photos/upload`,
      body,
    );
  }

  update(
    id: number,
    body: { caption?: string; sortOrder?: number; isCover?: boolean },
  ): Observable<VisitPhoto> {
    return this.api.put<VisitPhoto>(`visit-photos/${id}`, body);
  }

  remove(id: number): Observable<void> {
    return this.api.delete<void>(`visit-photos/${id}`);
  }

  reorder(ids: number[]): Observable<VisitPhoto[]> {
    return this.api.put<VisitPhoto[]>(`visit-photos/reorder`, { ids });
  }
}
